/**
 * Design Library — layout patterns (whole-composition strategies).
 * Pure design knowledge: no pixels, geometry, or editor concepts.
 */

import { definePattern } from './_define';

export const LAYOUT_PATTERNS = [
  definePattern({
    id: 'centered-poster', name: 'Centered Poster', family: 'layout',
    purpose: 'Everything composed on a single central axis.',
    communicationGoal: 'Deliver one message with ceremony and calm.',
    visualGoal: 'Symmetric, settled, unmistakably focused.',
    contentGoal: 'Headline, supporting line, action, footer — all centred.',
    marketingGoal: 'Announce with confidence; feel considered.',
    idealDocuments: ['Poster', 'Flyer', 'Invitation', 'Instagram Post'],
    idealIndustries: ['events', 'wedding', 'festival', 'fitness', 'retail'],
    idealAudiences: ['general-public', 'families'],
    idealGenomes: ['minimal', 'bold', 'retro', 'memphis', 'editorial'],
    compatiblePatterns: ['premium-cta', 'logo-strip', 'discount-block'],
    incompatiblePatterns: ['split-hero', 'bento-grid'],
    compositionPhilosophy: 'Central axis; symmetry does the work.',
    hierarchyPhilosophy: 'Vertical rhythm from the dominant headline downward.',
    typicalSections: ['headline', 'subheadline', 'cta', 'footer'],
    preferredComponents: ['hero', 'cta', 'footer'],
    premiumCues: ['generous-whitespace', 'confident-scale'],
    thingsToAvoid: ['centered-everything-without-hierarchy', 'clutter'],
    variationRules: ['type-scale', 'decoration-density'],
    capabilities: ['cta', 'text-led'],
    tags: ['centered', 'symmetric', 'layout'],
    expansionRules: { emphasis: 'headline', axis: 'center' },
  }),
  definePattern({
    id: 'editorial-columns', name: 'Editorial Columns', family: 'layout',
    purpose: 'A magazine-style column structure that unfolds a story.',
    communicationGoal: 'Give a longer message room to breathe and read.',
    visualGoal: 'Baseline-locked columns with refined type.',
    contentGoal: 'Headline, standfirst, body columns, pull quote.',
    marketingGoal: 'Build authority and considered credibility.',
    idealDocuments: ['Brochure', 'Presentation', 'Poster', 'Biodata'],
    idealIndustries: ['fashion', 'real-estate', 'legal', 'corporate', 'education'],
    idealAudiences: ['professionals', 'luxury-customers'],
    idealGenomes: ['editorial', 'swiss', 'luxury-fashion', 'luxury'],
    compatiblePatterns: ['image-band', 'testimonials', 'logo-strip'],
    incompatiblePatterns: ['instagram-story-hero', 'youtube-thumbnail-hero'],
    compositionPhilosophy: 'Strict columns on a baseline grid; type is the architecture.',
    hierarchyPhilosophy: 'Standfirst and pull quotes guide the read.',
    typicalSections: ['headline', 'standfirst', 'body', 'pull-quote'],
    preferredComponents: ['hero', 'features', 'footer'],
    premiumCues: ['refined-type', 'crafted-detail', 'generous-whitespace'],
    thingsToAvoid: ['default-fonts', 'cramped-measure'],
    variationRules: ['column-count', 'pull-quote-placement'],
    capabilities: ['multi-column', 'text-led', 'image'],
    tags: ['editorial', 'columns', 'layout'],
    expansionRules: { emphasis: 'body', columns: 'multi' },
  }),
  definePattern({
    id: 'bento-grid', name: 'Bento Grid', family: 'layout',
    purpose: 'Content compartmentalised into tiles of varying weight.',
    communicationGoal: 'Show many things at once without chaos.',
    visualGoal: 'Modular tiles that read as one considered system.',
    contentGoal: 'A hero tile plus feature, stat and image tiles.',
    marketingGoal: 'Demonstrate breadth and modernity.',
    idealDocuments: ['Presentation', 'Infographic', 'Instagram Post', 'Banner'],
    idealIndustries: ['technology', 'finance', 'corporate', 'education'],
    idealAudiences: ['professionals', 'students'],
    idealGenomes: ['bento', 'future-ui', 'modern', 'glassmorphism'],
    compatiblePatterns: ['stat-row', 'feature-row', 'gradient-hero'],
    incompatiblePatterns: ['centered-poster', 'poster-hero'],
    compositionPhilosophy: 'Modular tiles; the grid is visible and intentional.',
    hierarchyPhilosophy: 'One tile is hero-sized; the rest step down in weight.',
    typicalSections: ['hero-tile', 'feature-tile', 'stat-tile', 'image-tile'],
    preferredComponents: ['hero', 'features', 'stats', 'gallery'],
    premiumCues: ['precision', 'subtle-depth', 'crafted-detail'],
    thingsToAvoid: ['equal-weight-tiles', 'clutter'],
    variationRules: ['tile-count', 'hero-tile-position'],
    capabilities: ['multi-column', 'stats', 'image', 'gallery'],
    tags: ['bento', 'modular', 'layout'],
    expansionRules: { emphasis: 'hero-tile', grid: 'bento' },
  }),
  definePattern({
    id: 'minimal-stack', name: 'Minimal Stack', family: 'layout',
    purpose: 'A restrained vertical stack with generous air.',
    communicationGoal: 'Say less, mean more.',
    visualGoal: 'Calm vertical rhythm dominated by whitespace.',
    contentGoal: 'A few pieces of content, each given room.',
    marketingGoal: 'Read as premium and self-assured.',
    idealDocuments: ['Flyer', 'Poster', 'Invitation', 'Business Card', 'Instagram Post'],
    idealIndustries: ['jewelry', 'spa', 'salon', 'hotel', 'real-estate', 'medical'],
    idealAudiences: ['luxury-customers', 'professionals'],
    idealGenomes: ['minimal', 'scandinavian', 'luxury', 'luxury-fashion', 'premium-medical'],
    compatiblePatterns: ['image-band', 'logo-strip', 'premium-cta'],
    incompatiblePatterns: ['collage-wall'],
    compositionPhilosophy: 'Vertical stack; whitespace is the primary material.',
    hierarchyPhilosophy: 'A steep drop from one dominant element to quiet support.',
    typicalSections: ['headline', 'subheadline', 'cta'],
    preferredComponents: ['hero', 'cta', 'footer'],
    premiumCues: ['restraint', 'generous-whitespace', 'refined-type'],
    thingsToAvoid: ['clutter', 'busy-background'],
    variationRules: ['whitespace-ratio', 'alignment-axis'],
    capabilities: ['cta', 'text-led', 'image'],
    tags: ['minimal', 'stack', 'layout'],
    expansionRules: { emphasis: 'headline', density: 'airy' },
  }),
  definePattern({
    id: 'magazine-spread', name: 'Magazine Spread', family: 'layout',
    purpose: 'An editorial spread balancing a large image with typographic craft.',
    communicationGoal: 'Tell a story with the authority of print.',
    visualGoal: 'Image and type in an art-directed conversation.',
    contentGoal: 'Cover image, headline, standfirst, body, caption.',
    marketingGoal: 'Build aspiration and brand depth.',
    idealDocuments: ['Brochure', 'Presentation', 'Poster'],
    idealIndustries: ['fashion', 'hotel', 'travel', 'real-estate', 'wedding'],
    idealAudiences: ['luxury-customers', 'women', 'professionals'],
    idealGenomes: ['luxury-fashion', 'editorial', 'luxury'],
    compatiblePatterns: ['image-band', 'editorial-columns', 'testimonials'],
    incompatiblePatterns: ['gradient-hero', 'youtube-thumbnail-hero'],
    compositionPhilosophy: 'Asymmetric spread; the image sets the tone, type answers it.',
    hierarchyPhilosophy: 'Image, then headline, then the quiet detail.',
    typicalSections: ['image', 'headline', 'standfirst', 'body', 'caption'],
    preferredComponents: ['hero', 'gallery', 'footer'],
    premiumCues: ['refined-type', 'generous-whitespace', 'crafted-detail'],
    thingsToAvoid: ['clip-art', 'busy-background'],
    variationRules: ['image-side', 'column-count'],
    capabilities: ['image', 'multi-column', 'image-led'],
    tags: ['magazine', 'editorial', 'layout'],
    expansionRules: { emphasis: 'image', columns: 'multi' },
  }),
  definePattern({
    id: 'collage-wall', name: 'Collage Wall', family: 'layout',
    purpose: 'An expressive collage of overlapping fragments.',
    communicationGoal: 'Convey energy, culture and abundance.',
    visualGoal: 'Layered overlap with deliberate, controlled chaos.',
    contentGoal: 'Many images and fragments around one anchor.',
    marketingGoal: 'Feel alive and culturally current.',
    idealDocuments: ['Poster', 'Instagram Post', 'Banner'],
    idealIndustries: ['festival', 'music', 'events', 'fashion'],
    idealAudiences: ['students', 'general-public'],
    idealGenomes: ['memphis', 'bold', 'playful'],
    compatiblePatterns: ['poster-hero', 'discount-block'],
    incompatiblePatterns: ['minimal-stack', 'medical-services'],
    compositionPhilosophy: 'Layered overlap; chaos held together by one anchor.',
    hierarchyPhilosophy: 'One anchor element wins; fragments orbit it.',
    typicalSections: ['anchor', 'fragments', 'headline'],
    preferredComponents: ['hero', 'gallery'],
    premiumCues: ['crafted-detail'],
    thingsToAvoid: ['illegible-overlap', 'low-contrast'],
    variationRules: ['fragment-density', 'rotation-range'],
    capabilities: ['image', 'gallery', 'image-led'],
    tags: ['collage', 'maximal', 'layout'],
    expansionRules: { emphasis: 'anchor', density: 'dense' },
  }),
];
